<template>
  <div v-if="alertMessage.message !== ''">
    <template v-if="showRequestPassword">
      <div class="alert" :class="alertMessage.class">
        {{ alertMessage.message }}
        <button v-if="showRequestPassword" type="button" class="btn btn-primary" @click="openRequestPassword()">
          Password anfordern
        </button>
      </div>
    </template>
    <template v-else>
      <div class="alert" role="alert" :class="alertMessage.class" v-dompurify-html="alertMessage.message"></div>
    </template>
  </div>
  <div v-if="isLoading || (user && !user.virtual)" class="wdw-login-form wdw-form">
    <vue-loading v-if="isLoading" />
    <template v-if="user">
      <p>Eingeloggt als {{ user.public_name }}</p>
      <form @submit.prevent="logout">
        <button type="submit" class="btn btn-default btn-logout">Logout</button>
      </form>
    </template>
  </div>
  <div v-else>
    <Form @submit="onSubmitLoginForm">
      <div id="wdw-login-form" class="wdw-form">
        <form-control-input
          ref="primaryFocusElement"
          label="E-Mail-Adresse"
          type="email"
          name="email"
          code="email"
          :required="true"
          :public="true"
          :hidden="false"
          error="Geben Sie bitte eine gültige E-Mail-Adresse ein."
          help="Geben Sie hier Ihre E-Mail-Adresse ein mit der Sie sich bei uns registriert haben."
          parentFormId="wdw-login-form"
          defaultvalue=""
        />
        <form-control-input
          label="Passwort"
          type="password"
          name="password"
          code="password"
          :required="true"
          :public="true"
          :hidden="false"
          help="Geben Sie hier Ihr <strong>bestehendes</strong> Passwort ein."
          parentFormId="wdw-login-form"
          defaultvalue=""
        >
          <template v-if="showPwRequest" v-slot:dynamicLabel>
            (
            <a @click="openRequestPassword()" @keyup.enter="openRequestPassword()" tabindex="0" role="link">
              Passwort vergessen?
            </a>
            )
          </template>
        </form-control-input>
        <div class="d-grid gap-2 col-6 mx-auto">
          <button
            :disabled="data.loader.submitting_login"
            class="btn btn-primary btn-login-submit btn-form-submit"
            type="submit"
            name="submit"
          >
            Login
          </button>
          <vue-loading v-if="data.loader.submitting_login" />
        </div>
      </div>
    </Form>
    <template v-if="showRegistration">
      <a
        @click="openRegistration()"
        @keyup.enter="openRegistration()"
        class="switch-register-login-link to-register"
        tabindex="0"
        role="link"
      >
        Noch nicht registriert? Klicken Sie
        <b>hier</b>
        um sich zu registrieren.
      </a>
    </template>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import useAuthentication from '@/composables/user/useAuthentication';
import { onBeforeMount, reactive, ref, computed, initCustomFormatter } from 'vue';
import { RepositoryFactory } from '@/api/repositoryFactory';
const AuthRepository = RepositoryFactory.get('auth');

import { Form } from 'vee-validate';
import VueLoading from '@/components/general/Loading';
import FormControlInput from '@/components/general/FormControl/Input';

/* 
Still in development
upgrade to a "user" component
Should Contain:
    -[x] Login 
     -[ ] refactor to FormControl fields
     -[ ] refactor to switch between login/registration
    -[ ] Registration
    -[ ] Request Password / Forgot Password
    -[ ] User Profile
    -[ ] Editing User Profile
    -[ ] Redirect after Account Activation from Server
    -[ ] Redirect after Password reset from Server
*/

export default {
  name: 'Login',
  props: {
    domainid: {
      type: Number,
      required: true,
    },
    handleEventsInParent: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['open-registration', 'open-pw-request'],

  setup(props, context) {
    const primaryFocusElement = ref(null);
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
    });
    const data = reactive({
      // selected fields
      email_field: null,
      password_field: null,
      // Flags for loader
      loader: {
        submitting_login: false,
      },
    });
    const { setAuthToken } = useAuthentication();
    const err = ref('');
    const showRequestPassword = ref(false);
    const alertMessage = reactive({
      message: '',
      class: 'alert-info',
    });
    const localStorageKeyRedirUrl = 'redirect_after_login';

    //computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const isLoading = computed(() => {
      return userStore.isLoading();
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const userComponentUri = computed(() => {
      // For testing in localhost instead of changing widget links in admin backend
      // return "/#/?register=true";
      if (domain.value && domain.value.widget_links && domain.value.widget_links.user_component) {
        return domain.value.widget_links.user_component;
      }
      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        return '/login?login_select=register';
      }
      return false;
    });
    const registrationEnabled = computed(() => {
      return domain.value && domain.value.enable_registration;
    });
    const showRegistration = computed(() => {
      return (props.handleEventsInParent || Boolean(userComponentUri.value)) && registrationEnabled.value;
    });
    const showPwRequest = computed(() => {
      return props.handleEventsInParent || Boolean(userComponentUri.value);
    });

    //methods
    function resetAlertMessage() {
      alertMessage.message = '';
      alertMessage.class = 'alert-info';
    }
    function emitOpenRegistration() {
      context.emit('open-registration');
    }
    function getCurrentUri() {
      return window.location.origin + window.location.pathname;
    }
    function openRegistration() {
      // T20794: Save current URL in localstorage for redirect later after registering
      localStorage.setItem(localStorageKeyRedirUrl, window.location.href);

      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        document.location.href = '/login?login_select=register&referer=' + getCurrentUri();
      } else if (props.handleEventsInParent) {
        emitOpenRegistration();
      } else if (userComponentUri.value) {
        window.location = userComponentUri.value + '#/?register=true';
      } else {
        console.log('Fehler: openRegistration');
      }
    }
    function emitRequestPassword() {
      context.emit('open-pw-request');
    }
    function openRequestPassword() {
      if (props.handleEventsInParent) {
        emitRequestPassword();
      } else if (userComponentUri.value) {
        window.location = userComponentUri.value + '#/?request_password=true';
      } else {
        console.log('Fehler: openRequestPassword');
      }
    }

    const redirectAfterLogin = () => {
      const redirectUrl = localStorage.getItem(localStorageKeyRedirUrl);
      if (redirectUrl && redirectUrl != '') {
        localStorage.removeItem(localStorageKeyRedirUrl);
        window.location.href = redirectUrl;
      }
    };

    function loginPostSubmitServer(params) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/login';

      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const hiddenField = document.createElement('input');
          hiddenField.type = 'hidden';
          hiddenField.name = key;
          hiddenField.value = params[key];

          form.appendChild(hiddenField);
        }
      }
      document.body.appendChild(form);
      form.submit();
    }

    const onSubmitLoginForm = (values, actions) => {
      // Show loading and clear any server error
      data.loader.submitting_login = true;
      resetAlertMessage();
      showRequestPassword.value = false;

      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        // server-login via redirect
        values['submit-form'] = 1;
        values['referer'] = getCurrentUri();
        loginPostSubmitServer(values);
        redirectAfterLogin();
      } else {
        // login via api
        AuthRepository.login(values.email, values.password, props.domainid)
          .then(response => {
            if (response && response.data.success && response.data.data) {
              setAuthToken(response.data.data.token);
              alertMessage.message = 'Login erfolgreich.';
              actions.resetForm();
              data.loader.submitting_login = false;
              redirectAfterLogin();
            }
          })
          .catch(error => {
            alertMessage.message = 'Login ist leider fehlgeschlagen. Bitte versuchen Sie es erneut.';
            alertMessage.class = 'alert-warning';
            if (showPwRequest.value) {
              alertMessage.message =
                'Login ist leider fehlgeschlagen. Falls Sie Ihr Password vergessen haben können Sie ein Neues anfordern:';
              alertMessage.class = 'alert-warning';
              showRequestPassword.value = true;
            }

            data.loader.submitting_login = false;
          });
      }
    };

    const logout = () => {
      resetAlertMessage();
      if (process.env.VUE_APP_INTERNAL_COOKIE_USE) {
        // server-logout via redirect
        document.location.href = '/logout?referer=' + getCurrentUri();
      } else {
        AuthRepository.logout(userStore.getState().authToken)
          .then(response => {
            if (response && response.data.success && response.data.data) {
              setAuthToken();
            }
          })
          .catch(error => {
            console.error(error.response);
            // reset on error anyway
            setAuthToken();
            alertMessage.message = 'Logout ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
            alertMessage.class = 'alert-warning';
          });
      }
    };

    return {
      data,
      onSubmitLoginForm,
      logout,
      user,
      isLoggedIn,
      isLoading,
      alertMessage,
      showRequestPassword,
      showPwRequest,
      showRegistration,
      openRegistration,
      openRequestPassword,
      primaryFocusElement,
    };
  },

  components: {
    Form,
    VueLoading,
    FormControlInput,
  },
};
</script>

<style scoped>
#wdw-login-form {
  margin-bottom: 10px;
}
</style>
